/**
 * /agents — Ledger Core AI agent directory.
 *
 * Visual design mirrors the Bynaus Project Workspace agent grid:
 * section headers with counts, cards with icon / name / status dot /
 * description / stat footer.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_auth/agents")({
  component: AgentsPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

type AgentStatus = "live" | "beta" | "coming-soon";

interface AgentDef {
  id: string;
  name: string;
  status: AgentStatus;
  statusLabel?: string;          // override default label
  description: string;
  iconBg: string;                // inline style bg color
  iconColor: string;             // inline style text color
  iconEmoji: string;
  stat: string;                  // footer stat text
  statIcon: string;              // emoji icon for stat
  category: string;
  href?: string;                 // nav link
  chatPrompt?: string;
}

// ── Catalogue ─────────────────────────────────────────────────────────────────

const AGENTS: AgentDef[] = [
  // Core
  {
    id: "ask-bynaus",
    name: "Ask Bynaus",
    status: "live",
    description: "Natural-language interface to your entire ledger. Query balances, invoices, payroll, and job cost — or post journals and run approvals directly from chat.",
    iconBg: "#e8edf5", iconColor: "#1e3a5f", iconEmoji: "✦",
    stat: "13 tools available",
    statIcon: "🔧",
    category: "Core",
    href: "/chat",
  },

  // Payables
  {
    id: "ap-intake",
    name: "AP Intake",
    status: "live",
    description: "Reads PDFs, photos, and emails. Extracts vendor, amount, due date, and cost codes with per-field confidence. Zero manual entry for recurring vendors after 2–3 reviews.",
    iconBg: "#fff3e0", iconColor: "#e65100", iconEmoji: "📄",
    stat: "3 channels · OCR + email",
    statIcon: "📥",
    category: "Payables",
    href: "/ap/intake",
  },
  {
    id: "approvals-ai",
    name: "Approvals AI",
    status: "live",
    description: "Scores each approval for risk, recommends the right tier, and generates a plain-English summary for every reviewer. Learns from past decisions.",
    iconBg: "#fff8e1", iconColor: "#b45309", iconEmoji: "⚡",
    stat: "0 pending · AI routing on",
    statIcon: "✅",
    category: "Payables",
    href: "/approvals",
  },
  {
    id: "vendor-watch",
    name: "Vendor Spend Watch",
    status: "live",
    description: "Connects to Anthropic, AWS, Stripe, GitHub, and Vercel APIs. Snapshots MTD spend every 6 hours and fires an alert before the bill arrives.",
    iconBg: "#ede9fe", iconColor: "#6d28d9", iconEmoji: "📡",
    stat: "7 vendors watched · 1 alert",
    statIcon: "⚡",
    category: "Payables",
    chatPrompt: "Show me current MTD spend across all watched vendors and flag any that are trending over budget.",
  },

  // Payroll
  {
    id: "labor-anomaly",
    name: "Labor Anomaly Detector",
    status: "live",
    description: "Scans every labor event for statistical outliers — unexpected OT, unusual crew sizes, cost-code mismatches, and burden rate drift. Surfaces a prioritised alert queue.",
    iconBg: "#fde8e8", iconColor: "#c62828", iconEmoji: "🔍",
    stat: "Runs on every pay run",
    statIcon: "📊",
    category: "Payroll",
    href: "/payroll",
    chatPrompt: "Show me any labor cost anomalies from the last pay period.",
  },
  {
    id: "union-compliance",
    name: "Union Compliance Audit",
    status: "live",
    description: "Checks every pay run against your CBAs for misclassification, fringe underpayment, missing dues authorizations, and expiry alerts. Exports IBEW XML and UBC CSV.",
    iconBg: "#ede7f6", iconColor: "#5e35b1", iconEmoji: "🏛",
    stat: "42 test cases · L3 certified",
    statIcon: "📋",
    category: "Payroll",
    chatPrompt: "Run a union compliance audit for the last pay period.",
  },
  {
    id: "burden-forecast",
    name: "Burden Forecast",
    status: "live",
    description: "Uses historical burden rates and current crew composition to forecast total burdened labor cost through project completion. Flags jobs trending over budget.",
    iconBg: "#e0f2f1", iconColor: "#00695c", iconEmoji: "📈",
    stat: "Per-job · updates nightly",
    statIcon: "💡",
    category: "Payroll",
    chatPrompt: "Forecast burdened labor cost for our active projects.",
  },
  {
    id: "certified-payroll-ai",
    name: "Certified Payroll AI",
    status: "live",
    description: "Davis-Bacon prevailing wage monitor. Flags misclassifications, generates WH-347 reports, exports LCPtracker XML and CA DIR eCPR, writes an AI audit narrative per submission.",
    iconBg: "#e8f5e9", iconColor: "#2e7d32", iconEmoji: "📋",
    stat: "WH-347 · LCPtracker · DIR eCPR",
    statIcon: "🏛",
    category: "Payroll",
    href: "/payroll-l4",
  },

  // Receivables
  {
    id: "collection-agent",
    name: "Collection Agent",
    status: "live",
    description: "Monitors all outstanding AR, tracks aging buckets, and autonomously runs follow-up sequences — reminder emails, call logs, dispute captures, and escalation notices. Every promise tracked, every action logged.",
    iconBg: "#fffbeb", iconColor: "#b45309", iconEmoji: "💰",
    stat: "$247K outstanding · 3 past 30d",
    statIcon: "📬",
    category: "Receivables",
    chatPrompt: "Show me all overdue invoices past 30 days with the last follow-up action taken on each.",
  },
  {
    id: "credit-agent",
    name: "Credit Agent",
    status: "live",
    description: "Evaluates how much credit to extend to any GC or owner. Pulls Ledger Core payment history, cross-references D&B risk scores, analyses job-size vs. terms, and produces a recommended credit limit with risk tier. Human approval required above $100K.",
    iconBg: "#f0fdf4", iconColor: "#15803d", iconEmoji: "🛡️",
    stat: "11 customers rated · $1.84M exposure",
    statIcon: "📊",
    category: "Receivables",
    chatPrompt: "Summarise the credit profile and payment history for our top 5 GC customers.",
  },

  // Vendor Management
  {
    id: "vendor-onboarding",
    name: "Vendor Onboarding",
    status: "live",
    description: "Collects W-9, COI, banking details, trade classification, and compliance docs to activate a new vendor. Auto-parses uploaded certificates, flags expired or insufficient coverage, and routes to the right approver. Vendors go live in Ledger Core only after all gates pass.",
    iconBg: "#dbeafe", iconColor: "#1d4ed8", iconEmoji: "🏗️",
    stat: "3 in progress · avg 4.2d to approve",
    statIcon: "📋",
    category: "Vendor Management",
    chatPrompt: "Show me all vendors currently in the onboarding queue and what's blocking each one.",
  },
  {
    id: "sub-onboarding",
    name: "Sub Onboarding",
    status: "live",
    description: "Qualifies subcontractors end-to-end — CSLB license lookup, SAM.gov debarment check, CA DIR PWC registration, bond capacity, all insurance layers, union affiliations, and prevailing wage status. Subs cannot receive a PO or subcontract until all compliance gates pass.",
    iconBg: "#fff7ed", iconColor: "#c2410c", iconEmoji: "👷",
    stat: "2 in progress · 0 debarment hits YTD",
    statIcon: "🔍",
    category: "Vendor Management",
    chatPrompt: "What is the current qualification status for each subcontractor in onboarding, and what documents are still missing?",
  },

  // Job Cost
  {
    id: "contract-parser",
    name: "Contract Parser",
    status: "live",
    description: "Upload a contract PDF and AI extracts scope, schedule, payment terms, retainage, and liquidated damages into structured data. Flags non-standard clauses for review.",
    iconBg: "#e8edf5", iconColor: "#1e3a5f", iconEmoji: "🤝",
    stat: "PDF upload · instant parse",
    statIcon: "⚡",
    category: "Job Cost",
    href: "/contracts",
  },

  // Reporting
  {
    id: "ai-reports",
    name: "AI Report Builder",
    status: "live",
    description: "Describe the report you need in plain English. The AI builds it from live GL data — no SQL, no spreadsheets. Schedule delivery to your inbox.",
    iconBg: "#ede7f6", iconColor: "#5e35b1", iconEmoji: "📊",
    stat: "38 templates · scheduling on",
    statIcon: "📅",
    category: "Reporting",
    href: "/reports",
    chatPrompt: "Build me a job cost summary for all active projects.",
  },

  // Advisory
  {
    id: "accounting-expert",
    name: "Accounting Expert",
    status: "live",
    statusLabel: "Online",
    description: "Deep expertise in construction accounting — GAAP, percentage-of-completion, WIP schedules, AIA billing, retainage, job costing, and prevailing wage. Ask anything.",
    iconBg: "#fef3c7", iconColor: "#92400e", iconEmoji: "🎓",
    stat: "GAAP · WIP · AIA · Davis-Bacon",
    statIcon: "📚",
    category: "Advisory",
    chatPrompt:
      "You are a senior construction accounting expert with deep knowledge of GAAP, percentage-of-completion revenue recognition, WIP schedule analysis, AIA billing (G702/G703), retainage management, job costing, prevailing wage compliance (Davis-Bacon, state equivalents), union payroll, and construction-specific financial reporting. I have a question about my accounting:",
  },
];

const SECTIONS: { key: string; label: string; iconEmoji: string; iconBg: string; iconColor: string; sub: string }[] = [
  { key: "Core",      label: "CORE",          iconEmoji: "✦",  iconBg: "#e8edf5", iconColor: "#1e3a5f", sub: "Foundation agent — always on" },
  { key: "Payables",  label: "PAYABLES",      iconEmoji: "📄", iconBg: "#fff3e0", iconColor: "#e65100", sub: "AP intake, approvals, vendor spend" },
  { key: "Payroll",   label: "PAYROLL",       iconEmoji: "💰", iconBg: "#e8f5e9", iconColor: "#2e7d32", sub: "L2 labor · L3 union · L4 Davis-Bacon" },
  { key: "Receivables",       label: "RECEIVABLES",      iconEmoji: "💰", iconBg: "#fffbeb", iconColor: "#b45309", sub: "Collections, aging, and customer credit" },
  { key: "Vendor Management", label: "VENDOR MANAGEMENT", iconEmoji: "🏗️", iconBg: "#dbeafe", iconColor: "#1d4ed8", sub: "Vendor & subcontractor onboarding and compliance" },
  { key: "Job Cost",  label: "JOB COST",      iconEmoji: "🏗",  iconBg: "#e8edf5", iconColor: "#1e3a5f", sub: "Contract parsing, WIP intelligence" },
  { key: "Reporting", label: "REPORTING",     iconEmoji: "📊", iconBg: "#ede7f6", iconColor: "#5e35b1", sub: "AI-generated financial statements" },
  { key: "Advisory",  label: "ADVISORY",      iconEmoji: "🎓", iconBg: "#fef3c7", iconColor: "#92400e", sub: "Industry expert on demand" },
];

// ── Page ──────────────────────────────────────────────────────────────────────

function AgentsPage() {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const liveCount = AGENTS.filter((a) => a.status === "live").length;
  const openChat  = (prompt?: string) =>
    window.dispatchEvent(new CustomEvent("bynaus:open-agent", { detail: { prompt } }));

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">

      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 dark:bg-gray-900 dark:border-gray-800 px-6 py-4 flex-shrink-0 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <span style={{ color: "#4f46e5" }}>✦</span> Your Agents
            <span className="ml-1 text-sm font-normal px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              {AGENTS.length}
            </span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {liveCount} live · powered by AI
          </p>
        </div>
        <button
          onClick={openChat}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors"
          style={{ background: "#4f46e5" }}
          onMouseOver={(e) => (e.currentTarget.style.background = "#4338ca")}
          onMouseOut={(e) => (e.currentTarget.style.background = "#4f46e5")}
        >
          <span>✦</span> Ask Bynaus
        </button>
      </div>

      {/* ── Content ── */}
      <div className="overflow-y-auto flex-1 px-6 py-6 bg-slate-50 dark:bg-gray-950">
        {SECTIONS.map((section) => {
          const agents = AGENTS.filter((a) => a.category === section.key);
          if (agents.length === 0) return null;
          const isCollapsed = collapsed.has(section.key);

          return (
            <div key={section.key} className="mb-6">
              {/* Section header */}
              <div
                className="flex items-center gap-2.5 mb-3 pb-2 border-b border-slate-200 dark:border-gray-800 cursor-pointer select-none"
                onClick={() => toggle(section.key)}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0"
                  style={{ background: section.iconBg }}
                >
                  <span style={{ color: section.iconColor }}>{section.iconEmoji}</span>
                </div>
                <span className="text-[13px] font-bold text-slate-800 dark:text-slate-100 tracking-wide">
                  {section.label}
                </span>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {agents.length}
                </span>
                <span className="text-[11.5px] text-slate-400 dark:text-slate-500 ml-auto hidden sm:block">
                  {section.sub}
                </span>
                <span
                  className="w-6 h-6 flex items-center justify-center text-slate-400 dark:text-slate-500 flex-shrink-0 transition-transform duration-200"
                  style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}
                >
                  ▾
                </span>
              </div>

              {/* Cards grid */}
              {!isCollapsed && (
                <div
                  className="grid gap-3.5"
                  style={{ gridTemplateColumns: "repeat(auto-fill, minmax(215px, 1fr))" }}
                >
                  {agents.map((agent) => (
                    <AgentCard key={agent.id} agent={agent} onChat={openChat} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Agent card ─────────────────────────────────────────────────────────────────

function AgentCard({ agent, onChat }: { agent: AgentDef; onChat: (prompt?: string) => void }) {
  const isLive  = agent.status === "live";
  const isBeta  = agent.status === "beta";
  const dimmed  = agent.status === "coming-soon";

  const inner = (
    <div
      className={`bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-[10px] p-4 flex flex-col gap-2 min-h-[150px] transition-all duration-150 ${
        dimmed ? "opacity-55" : "cursor-pointer hover:border-[#4f7cff] dark:hover:border-[#4f7cff]"
      }`}
      style={dimmed ? {} : undefined}
      onMouseOver={(e) => {
        if (!dimmed) {
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 14px rgba(79,124,255,0.12)";
          (e.currentTarget as HTMLDivElement).style.transform = "translateY(-1px)";
        }
      }}
      onMouseOut={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = "";
        (e.currentTarget as HTMLDivElement).style.transform = "";
      }}
    >
      {/* Row: icon + name + status */}
      <div className="flex items-center gap-3">
        <div
          className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center flex-shrink-0 text-base"
          style={{ background: agent.iconBg }}
        >
          <span style={{ color: agent.iconColor }}>{agent.iconEmoji}</span>
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-slate-900 dark:text-slate-100 leading-snug truncate">
            {agent.name}
          </div>
          {isLive && <StatusOnline label={agent.statusLabel ?? "Online"} />}
          {isBeta && <StatusOnline label="Beta" color="#b45309" pulse={false} />}
          {dimmed && (
            <div className="text-[11px] text-slate-400 dark:text-slate-500">Coming soon</div>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed flex-1">
        {agent.description}
      </p>

      {/* Footer stat */}
      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500 pt-2 border-t border-slate-100 dark:border-gray-800">
        <span>{agent.statIcon}</span>
        <span>{agent.stat}</span>
      </div>
    </div>
  );

  if (dimmed) return inner;

  // Clickable: prefer navigation link, fall back to opening chat
  if (agent.href) {
    return (
      <Link to={agent.href as any} className="no-underline block">
        {inner}
      </Link>
    );
  }

  return (
    <div onClick={() => onChat(agent.chatPrompt)} className="block">
      {inner}
    </div>
  );
}

// ── Pulsing online indicator ───────────────────────────────────────────────────

function StatusOnline({
  label,
  color = "#2e7d32",
  pulse = true,
}: {
  label: string;
  color?: string;
  pulse?: boolean;
}) {
  return (
    <div className="flex items-center gap-1 text-[11px]" style={{ color }}>
      <span
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{
          background: color,
          animation: pulse ? "lc-pulse-green 2s infinite" : undefined,
        }}
      />
      {label}
      {/* Inline keyframe — Tailwind doesn't ship this pulse variant */}
      {pulse && (
        <style>{`
          @keyframes lc-pulse-green {
            0%   { box-shadow: 0 0 0 0 ${color}80; }
            70%  { box-shadow: 0 0 0 5px ${color}00; }
            100% { box-shadow: 0 0 0 0 ${color}00; }
          }
        `}</style>
      )}
    </div>
  );
}
